import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  Copy as CopyIcon,
  Edit as EditIcon,
  ExternalLink as ExternalLinkIcon,
  FileText as FileTextIcon,
  PlusCircle as PlusCircleIcon,
  Trash2 as TrashIcon,
} from 'react-feather';
import { useSnackbar } from 'notistack';
import Page from '../../../components/Layout/Page';
import thousandSeparator from '../../../utils/thousandSeparator';
import {
  getPaymentLinks,
  deletePaymentLink,
} from '../../../requests/api/paymentLinks';
import handleApiResponse from '../../../utils/handleApiResponse';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  filterLabel: {
    color: theme.palette.primary.main,
    fontSize: '0.75rem',
    marginBottom: theme.spacing(0.5),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
}));

const STATUS_OPTIONS = ['todos', 'activo', 'expirado', 'cancelado'];
const TYPE_OPTIONS = ['todos', 'unico'];

const STATUS_CHIP = {
  activo: { label: 'Activo', style: { backgroundColor: '#4caf50', color: '#fff' } },
  expirado: { label: 'Expirado', style: { backgroundColor: '#ff9800', color: '#fff' } },
  cancelado: { label: 'Cancelado', style: { backgroundColor: '#f44336', color: '#fff' } },
};

function formatDate(isoString) {
  return isoString.substring(0, 10).split('-').reverse().join('/');
}

export default function PaymentLinksView() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const account = useSelector((s) => s.account);

  const [links, setLinks] = useState([]);
  const [count, setCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterType, setFilterType] = useState('todos');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [descriptionLink, setDescriptionLink] = useState(null);

  const fetchLinks = async () => {
    try {
      const response = await getPaymentLinks(
        page,
        limit,
        filterStatus === 'todos' ? '' : filterStatus,
        filterType === 'todos' ? '' : filterType,
        account.user.clientId,
      );
      setLinks(response.paymentLinks || []);
      setCount(response.totalLinks || 0);
    } catch (err) {
      handleApiResponse(enqueueSnackbar, err, false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [page, limit, filterStatus, filterType]);

  const handleCopyUrl = (url) => {
    // eslint-disable-next-line no-undef
    window.navigator.clipboard.writeText(url);
    enqueueSnackbar('URL copiada al portapapeles', { variant: 'success' });
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deletePaymentLink(linkToDelete.id);
      handleApiResponse(enqueueSnackbar, response, true);
      await fetchLinks();
    } catch (err) {
      handleApiResponse(enqueueSnackbar, err, false);
    } finally {
      setLinkToDelete(null);
    }
  };

  return (
    <Page className={classes.root} title="Links de Pago">
      <Container maxWidth={false}>
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h3" color="textSecondary">
            Links de Pago
          </Typography>
          <Button
            color="primary"
            variant="contained"
            component={RouterLink}
            to="/links-de-pago/crear"
            startIcon={
              <SvgIcon fontSize="small">
                <PlusCircleIcon />
              </SvgIcon>
            }
          >
            Crear Link
          </Button>
        </Box>

        <Card>
          <Box p={2} minHeight={56} display="flex" alignItems="flex-end">
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item md={2} xs={12}>
                <InputLabel className={classes.filterLabel}>Estado</InputLabel>
                <Select
                  fullWidth
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
                  variant="outlined"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item md={2} xs={12}>
                <InputLabel className={classes.filterLabel}>Tipo</InputLabel>
                <Select
                  fullWidth
                  value={filterType}
                  onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
                  variant="outlined"
                >
                  {TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </Box>
        </Card>

        <Box mt={2}>
          <TableContainer component={Card}>
            <Box style={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Fecha de Creación</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha de Expiración</TableCell>
                    <TableCell style={{ width: 80 }}>Monto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Moneda</TableCell>
                    <TableCell>Max Usos</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">
                      <TablePagination
                        component="div"
                        count={count}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setLimit(Number(e.target.value));
                          setPage(0);
                        }}
                        page={page}
                        rowsPerPage={limit}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        labelRowsPerPage="Filas por página"
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {links.length > 0 ? links.map((link) => {
                    const chipDef = STATUS_CHIP[link.status] || {};
                    const isCancelled = link.status === 'cancelado';
                    const cancelledStyle = isCancelled ? { color: '#bdbdbd' } : {};
                    return (
                      <TableRow hover key={link.id} style={{ height: 60 }}>
                        <TableCell>
                          <Typography variant="h6" style={cancelledStyle}>
                            {link.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" style={cancelledStyle}>
                            {link.externalId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {isCancelled ? (
                            <Typography
                              variant="h6"
                              style={{
                                color: '#bdbdbd',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {link.url}
                            </Typography>
                          ) : (
                            <Tooltip title="Copiar URL">
                              <Typography
                                variant="h6"
                                color="inherit"
                                onClick={() => handleCopyUrl(link.url)}
                                style={{
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                }}
                              >
                                {link.url}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" style={cancelledStyle}>
                            {formatDate(link.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={chipDef.label || link.status}
                            size="small"
                            style={chipDef.style || {}}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" style={cancelledStyle}>
                            {formatDate(link.expiresAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" style={{ ...cancelledStyle, width: 80 }}>
                            ${' '}{thousandSeparator(link.amount, '.')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" style={cancelledStyle}>
                            {link.type.charAt(0).toUpperCase() + link.type.slice(1)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" style={cancelledStyle}>
                            {link.currency}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" style={cancelledStyle}>
                            {link.maxUses}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {link.description && (
                            <Tooltip title="Ver descripción">
                              <IconButton onClick={() => setDescriptionLink(link)}>
                                <SvgIcon fontSize="small">
                                  <FileTextIcon />
                                </SvgIcon>
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right" style={{ padding: '4px 16px' }}>
                          {isCancelled && <Box style={{ height: 48 }} />}
                          {!isCancelled && (
                            <>
                              <Tooltip title="Editar">
                                <IconButton
                                  component={RouterLink}
                                  to={`/links-de-pago/${link.id}/editar`}
                                >
                                  <SvgIcon fontSize="small">
                                    <EditIcon />
                                  </SvgIcon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton
                                  className={classes.deleteButton}
                                  onClick={() => setLinkToDelete(link)}
                                >
                                  <SvgIcon fontSize="small">
                                    <TrashIcon />
                                  </SvgIcon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copiar URL">
                                <IconButton onClick={() => handleCopyUrl(link.url)}>
                                  <SvgIcon fontSize="small">
                                    <CopyIcon />
                                  </SvgIcon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Abrir link">
                                <IconButton
                                  component="a"
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <SvgIcon fontSize="small">
                                    <ExternalLinkIcon />
                                  </SvgIcon>
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell align="center" colSpan={11}>
                        No hay links de pago para mostrar
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </Box>
      </Container>

      <Dialog open={Boolean(descriptionLink)} onClose={() => setDescriptionLink(null)}>
        <DialogTitle>
          {descriptionLink && descriptionLink.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ whiteSpace: 'pre-wrap' }}>
            {descriptionLink && descriptionLink.description}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionLink(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(linkToDelete)} onClose={() => setLinkToDelete(null)}>
        <DialogTitle>Eliminar link de pago</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar este link? Esta acción no se puede deshacer.
          </DialogContentText>
          {linkToDelete && (
            <DialogContentText>
              <strong>{linkToDelete.url}</strong>
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkToDelete(null)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
